import { ArrowLeft, AtSign, KeyRound, Lock, Mail, MessagesSquare, Mic, ShieldCheck, User } from '@/components/ui/icons';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { APP_NAME, forgotPasswordSchema, LEGAL, loginSchema, oauthStartSchema, registerSchema, resetPasswordSchema, twoFactorCodeInputSchema } from '@clanfire/shared';
import { ApiError, errorMessage } from '@/api/client';
import { authApi } from '@/api/endpoints';
import { useHumanCheck, WAIT_FOR_CHECK } from '@/components/auth/HumanCheck';
import { OAuthButtons, oauthErrorMessage } from '@/components/auth/OAuthButtons';
import { Logo, Wordmark } from '@/components/Brand';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useSubmit, validate } from '@/hooks/useForm';
import { logout } from '@/lib/session';
import { useAuth } from '@/stores/auth';
import { toast } from '@/stores/ui';

function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-dvh bg-sidebar">
      <aside className="relative hidden w-[46%] max-w-[640px] overflow-hidden bg-rail p-12 text-rail-fg lg:flex lg:flex-col">
        <div
          aria-hidden
          className="absolute -bottom-40 -left-24 h-[520px] w-[520px] rounded-full opacity-60 blur-3xl"
          style={{ background: 'radial-gradient(circle, var(--clanfire-accent) 0%, color-mix(in srgb, var(--clanfire-accent) 60%, transparent) 35%, transparent 70%)' }}
        />
        <div aria-hidden className="absolute top-24 -right-32 h-72 w-72 rounded-full opacity-25 blur-3xl" style={{ background: 'var(--clanfire-accent)' }} />
        <Wordmark className="relative" size={40} />
        <div className="relative mt-auto mb-10">
          <h2 className="text-[42px] leading-[1.1]">
            Your servers.
            <br />
            Your people.
          </h2>
          <p className="mt-4 max-w-md text-lg text-rail-fg/75">Text channels, voice rooms, DMs and screen sharing, in real time and free.</p>
          <ul className="mt-8 space-y-3 text-rail-fg/85">
            {[
              [<MessagesSquare key="m" size={18} />, 'Channels with replies, reactions, pins and link previews'],
              [<Mic key="v" size={18} />, 'Voice rooms with video and screen sharing'],
              [<ShieldCheck key="s" size={18} />, 'Roles, permissions and moderation built in'],
            ].map(([icon, text]) => (
              <li key={String(text)} className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rail-fg/10 text-rail-muted">{icon}</span>
                {text}
              </li>
            ))}
          </ul>
        </div>
      </aside>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-[420px] animate-slide-up">
          <div className="mb-8 flex justify-center lg:hidden">
            <Logo size={48} />
          </div>
          <h1 className="text-center text-3xl text-fg">{title}</h1>
          <p className="mt-2 text-center text-fg-muted">{subtitle}</p>
          <div className="mt-8 rounded-3xl bg-surface p-6 shadow-pop sm:p-8">{children}</div>
          {footer && <div className="mt-6 text-center text-sm text-fg-muted">{footer}</div>}
          <nav aria-label="Legal" className="mt-6 flex justify-center gap-4 text-xs text-fg-muted">
            <Link to="/privacy" className="hover:text-fg hover:underline">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-fg hover:underline">
              Terms
            </Link>
            <Link to="/cookies" className="hover:text-fg hover:underline">
              Cookies
            </Link>
          </nav>
        </div>
      </main>
    </div>
  );
}

/**
 * Coming back from Google or GitHub: the reason it didn't work (in the
 * query, e.g. ?oauthError=not_linked&provider=google), removed from the address bar once read.
 */
function useOAuthError(setError: (message: string | null) => void) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const message = oauthErrorMessage(params.get('oauthError'), params.get('provider'));
    if (!message) return;
    setError(message);
    setParams(
      (p) => {
        p.delete('oauthError');
        p.delete('provider');
        return p;
      },
      { replace: true },
    );
  }, [params, setParams, setError]);
}

/** A same-app path to go to after signing in with a provider (the server checks it again). */
const safeNext = (next: string | null) => (next && oauthStartSchema.shape.next.safeParse(next).success ? next : undefined);

function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-2xl border border-danger/40 bg-danger-soft px-4 py-2.5 text-sm font-medium text-danger">
      {message}
    </div>
  );
}

export function LoginPage() {
  const [values, setValues] = useState({ login: '', password: '' });
  /** Set once the password was right on an account with two-factor sign-in: the code step. */
  const [ticket, setTicket] = useState<string | null>(null);
  const [params] = useSearchParams();
  const { loading, error, fields, setFields, setError, run } = useSubmit();
  const setSession = useAuth((s) => s.setSession);
  const check = useHumanCheck('login');
  useOAuthError(setError);

  // Signed in with a provider on an account with two-factor sign-in: the code step.
  // (The ticket comes in the fragment, so it never reaches a server log.)
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const match = /^#oauth2fa=([^&]+)$/.exec(location.hash);
    if (!match) return;
    setTicket(decodeURIComponent(match[1]!));
    navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location, navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(loginSchema, values);
    if (!data) return setFields(invalid);
    if (!check.ready) return setError(WAIT_FOR_CHECK);
    const res = await run(() => authApi.login({ ...data, captchaToken: check.token }));
    check.reset();
    if (!res) return;
    if ('ticket' in res) setTicket(res.ticket);
    else setSession(res.user, res.settings);
  };

  if (ticket) {
    return (
      <TwoFactorStep
        ticket={ticket}
        onRestart={(message) => {
          setTicket(null);
          setValues((v) => ({ ...v, password: '' }));
          setError(message);
        }}
      />
    );
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Log in to pick up where you left off."
      footer={
        <>
          New to {APP_NAME}?{' '}
          <Link to="/register" className="font-semibold text-link hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormError message={error} />
        <Input
          label="Email or username"
          autoComplete="username"
          autoFocus
          leading={<AtSign size={16} />}
          value={values.login}
          onChange={(e) => setValues({ ...values, login: e.target.value })}
          error={fields.login}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          leading={<Lock size={16} />}
          value={values.password}
          onChange={(e) => setValues({ ...values, password: e.target.value })}
          error={fields.password}
          required
        />
        <Link to="/forgot-password" className="-mt-1 self-start text-sm font-medium text-link hover:underline">
          Forgot your password?
        </Link>
        {check.element}
        <Button type="submit" size="lg" block loading={loading}>
          Log in
        </Button>
        <OAuthButtons mode="login" next={safeNext(params.get('next'))} onError={setError} />
      </form>
    </AuthLayout>
  );
}

/** The second step of logging in: a code from the authenticator app, or a backup code. */
function TwoFactorStep({ ticket, onRestart }: { ticket: string; onRestart: (message: string | null) => void }) {
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  const { loading, error, fields, setFields, run } = useSubmit();
  const setSession = useAuth((s) => s.setSession);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(twoFactorCodeInputSchema, { code });
    if (!data) return setFields(invalid);
    const res = await run(async () => {
      try {
        return await authApi.loginTwoFactor(ticket, data.code);
      } catch (err) {
        // Too slow, or too many wrong codes: back to the password.
        if (err instanceof ApiError && err.code === 'TWO_FACTOR_EXPIRED') return void onRestart(err.message);
        throw err;
      }
    });
    if (res) setSession(res.user, res.settings);
  };

  const switchMode = () => {
    setBackup(!backup);
    setCode('');
    setFields({});
  };

  return (
    <AuthLayout
      title="Check it's you"
      subtitle={backup ? 'Enter one of your backup codes.' : 'Enter the 6-digit code from your authenticator app.'}
      footer={
        <>
          Lost your phone and your backup codes? Email{' '}
          <a href={`mailto:${LEGAL.contactEmail}`} className="font-semibold text-link hover:underline">
            {LEGAL.contactEmail}
          </a>{' '}
          from your account&apos;s email address.
        </>
      }
    >
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
        <FormError message={error} />
        <Input
          key={backup ? 'backup' : 'app'}
          label={backup ? 'Backup code' : '6-digit code'}
          autoComplete="one-time-code"
          inputMode={backup ? 'text' : 'numeric'}
          autoFocus
          placeholder={backup ? 'xxxxx-xxxxx' : '123456'}
          leading={<KeyRound size={16} />}
          value={code}
          onChange={(e) => setCode(backup ? e.target.value.slice(0, 24) : e.target.value.replace(/\D/g, '').slice(0, 6))}
          error={fields.code}
          required
        />
        <button type="button" onClick={switchMode} className="-mt-1 self-start text-sm font-medium text-link hover:underline">
          {backup ? 'Use your authenticator app instead' : 'Use a backup code instead'}
        </button>
        <Button type="submit" size="lg" block loading={loading}>
          Log in
        </Button>
        <Button variant="ghost" icon={<ArrowLeft size={16} />} onClick={() => onRestart(null)}>
          Back
        </Button>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const [params] = useSearchParams();
  const [values, setValues] = useState({ email: '', displayName: '', username: '', password: '' });
  const [inviteCode, setInviteCode] = useState(() => params.get('invite') ?? '');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { loading, error, fields, setFields, setError, run } = useSubmit();
  const setSession = useAuth((s) => s.setSession);
  const { registration, emailVerification } = useAuth((s) => s.config);
  const check = useHumanCheck('register');
  useOAuthError(setError);
  const inviteOnly = registration === 'invite';
  const set = (key: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement>) => setValues({ ...values, [key]: e.target.value });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(registerSchema, {
      ...values,
      displayName: values.displayName.trim() || undefined,
      inviteCode: inviteCode.trim() || undefined,
      acceptTerms,
    });
    if (!data) return setFields(invalid);
    if (!check.ready) return setError(WAIT_FOR_CHECK);
    const res = await run(() => authApi.register({ ...data, captchaToken: check.token }));
    check.reset();
    if (res) {
      setSession(res.user, res.settings);
      toast.success(
        emailVerification === 'off' ? `Welcome to ${APP_NAME}, ${res.user.displayName}!` : `Welcome, ${res.user.displayName}! We've sent you an email to confirm your address.`,
      );
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle={inviteOnly ? `${APP_NAME} is invite-only right now.` : 'It only takes a moment.'}
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-link hover:underline">
            Log in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormError message={error} />
        {(inviteOnly || inviteCode) && (
          <Input
            label="Invite code"
            leading={<KeyRound size={16} />}
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            error={fields.inviteCode}
            hint={params.get('invite') ? 'From your invite link.' : 'Paste the code or open the invite link a friend sent you.'}
            autoComplete="off"
            spellCheck={false}
            maxLength={64}
            required={inviteOnly}
          />
        )}
        <Input label="Email" type="email" autoComplete="email" autoFocus leading={<Mail size={16} />} value={values.email} onChange={set('email')} error={fields.email} required />
        <Input
          label="Display name"
          autoComplete="nickname"
          leading={<User size={16} />}
          value={values.displayName}
          onChange={set('displayName')}
          error={fields.displayName}
          hint="How others see you. You can change it any time."
          maxLength={32}
        />
        <Input
          label="Username"
          autoComplete="username"
          leading={<AtSign size={16} />}
          value={values.username}
          onChange={set('username')}
          error={fields.username}
          hint="Letters, numbers, underscores and periods."
          maxLength={32}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          leading={<Lock size={16} />}
          value={values.password}
          onChange={set('password')}
          error={fields.password}
          hint="At least 8 characters."
          required
        />
        <div className="flex flex-col gap-1.5">
          <label className="flex cursor-pointer items-start gap-2.5 rounded-2xl px-1 text-sm leading-snug text-fg-soft">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              aria-invalid={!!fields.acceptTerms || undefined}
              aria-describedby={fields.acceptTerms ? 'accept-terms-error' : undefined}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-(--clanfire-accent)"
            />
            <span>
              I'm {LEGAL.minimumAge} or older and I agree to the{' '}
              <Link to="/terms" target="_blank" className="font-semibold text-link hover:underline">
                Terms of Service
              </Link>
              . I've read the{' '}
              <Link to="/privacy" target="_blank" className="font-semibold text-link hover:underline">
                Privacy Policy
              </Link>
              .
            </span>
          </label>
          {fields.acceptTerms && (
            <p id="accept-terms-error" role="alert" className="px-1 text-xs font-medium text-danger">
              {fields.acceptTerms}
            </p>
          )}
        </div>
        {check.element}
        <Button type="submit" size="lg" block loading={loading}>
          Create account
        </Button>
        <OAuthButtons
          mode="signup"
          inviteCode={inviteCode}
          acceptTerms={acceptTerms}
          disabledReason={
            !acceptTerms
              ? 'Tick the box to agree to the Terms first.'
              : inviteOnly && !inviteCode.trim()
                ? 'Enter your invite code first.'
                : null
          }
          onError={setError}
        />
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { loading, error, fields, setFields, setError, run } = useSubmit();
  const check = useHumanCheck('forgot');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(forgotPasswordSchema, { email });
    if (!data) return setFields(invalid);
    if (!check.ready) return setError(WAIT_FOR_CHECK);
    const ok = await run(() => authApi.forgotPassword(data.email, check.token));
    check.reset();
    if (ok) setSent(true);
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link to choose a new one."
      footer={
        <Link to="/login" className="inline-flex items-center gap-1 font-semibold text-link hover:underline">
          <ArrowLeft size={14} /> Back to login
        </Link>
      }
    >
      {sent ? (
        <div className="flex flex-col items-center gap-3 text-center" role="status">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent">
            <Mail size={22} />
          </div>
          <p className="font-semibold text-fg">Check your inbox</p>
          <p className="text-sm text-fg-muted">
            If an account exists for <strong className="text-fg-soft">{email}</strong>, a reset link is on its way. It expires in one hour.
          </p>
          <p className="text-xs text-fg-muted">Running locally without SMTP? The link is printed in the server console.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <FormError message={error} />
          <Input label="Email" type="email" autoComplete="email" autoFocus leading={<Mail size={16} />} value={email} onChange={(e) => setEmail(e.target.value)} error={fields.email} required />
          {check.element}
          <Button type="submit" size="lg" block loading={loading}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const { loading, error, fields, setFields, run } = useSubmit();
  const navigate = useNavigate();
  const clear = useAuth((s) => s.clear);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) return setFields({ confirm: 'Passwords do not match.' });
    const { data, fields: invalid } = validate(resetPasswordSchema, { token, password });
    if (!data) return setFields(invalid.token ? { password: 'This reset link is invalid.' } : invalid);
    const ok = await run(async () => {
      await authApi.resetPassword(data.token, data.password);
      return true;
    });
    if (ok) {
      clear();
      toast.success('Password updated. Log in with your new password.');
      navigate('/login', { replace: true });
    }
  };

  return (
    <AuthLayout title="Choose a new password" subtitle="For security, this signs you out everywhere else.">
      {!token ? (
        <FormError message="This reset link is missing its token. Request a new one from the login page." />
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <FormError message={error} />
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            autoFocus
            leading={<KeyRound size={16} />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fields.password}
            hint="At least 8 characters."
          />
          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            leading={<KeyRound size={16} />}
            value={confirmPassword}
            onChange={(e) => setConfirm(e.target.value)}
            error={fields.confirm}
          />
          <Button type="submit" size="lg" block loading={loading}>
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

/** Shown instead of the app until a new account confirms its email (when that's required). */
export function VerifyEmailGate() {
  const user = useAuth((s) => s.user)!;
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Clicking the link (even on another device) lets you straight in: we check every few
  // seconds, and as soon as you come back to this tab.
  useEffect(() => {
    const check = () => {
      if (document.hidden) return;
      authApi
        .me()
        .then(({ user: fresh }) => fresh.emailVerified && useAuth.getState().setUser(fresh))
        .catch(() => undefined);
    };
    const timer = setInterval(check, 5000);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const resend = async () => {
    setSending(true);
    try {
      await authApi.resendVerification();
      toast.success(`Sent! Check ${user.email}.`);
      setCooldown(60);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthLayout title="Confirm your email" subtitle={`We sent a link to ${user.email}.`}>
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Mail size={26} />
        </div>
        <p className="text-sm leading-relaxed text-fg-soft">
          Click the link in the email to finish setting up your account. It can take a minute to arrive, so check your spam folder too.
        </p>
        <Button block loading={sending} disabled={cooldown > 0} onClick={() => void resend()}>
          {cooldown > 0 ? `Send again in ${cooldown}s` : 'Send the email again'}
        </Button>
        <Button variant="ghost" block onClick={() => void logout()}>
          Log out
        </Button>
        <p className="text-xs text-fg-muted">Wrong email address? Log out and sign up again with the right one.</p>
      </div>
    </AuthLayout>
  );
}

/** Where the link in the confirmation email lands. */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<{ status: 'working' | 'done' | 'failed'; message?: string }>({ status: 'working' });
  const signedIn = useAuth((s) => s.status === 'authenticated');
  const started = useRef(false);

  useEffect(() => {
    // Links are single-use: make sure it's only sent once (React may run effects twice in development).
    if (started.current) return;
    started.current = true;
    if (!token) return setState({ status: 'failed', message: 'This link is missing its code. Open it straight from the email.' });
    authApi
      .verifyEmail(token)
      .then(async () => {
        setState({ status: 'done' });
        if (useAuth.getState().status === 'authenticated') {
          const { user } = await authApi.me().catch(() => ({ user: null }));
          if (user) useAuth.getState().setUser(user);
        }
      })
      .catch((err) => setState({ status: 'failed', message: errorMessage(err) }));
  }, [token]);

  return (
    <AuthLayout
      title={state.status === 'done' ? 'Email confirmed' : state.status === 'failed' ? "That didn't work" : 'Confirming…'}
      subtitle={state.status === 'done' ? `Thanks! Your ${APP_NAME} account is all set.` : state.status === 'failed' ? (state.message ?? '') : 'One moment.'}
    >
      <div className="flex flex-col items-center gap-4 text-center">
        {state.status === 'working' ? (
          <Spinner size={24} />
        ) : (
          <Link to={signedIn ? '/app/@me' : '/login'} className="w-full">
            <Button block>{signedIn ? `Open ${APP_NAME}` : 'Log in'}</Button>
          </Link>
        )}
        {state.status === 'failed' && signedIn && <p className="text-xs text-fg-muted">You can send yourself a new link from the app.</p>}
      </div>
    </AuthLayout>
  );
}
